const collectionValues = (value) => {
    if (Array.isArray(value)) return value;
    if (value && typeof value === 'object') return Object.values(value);
    return [];
};

const firstNonEmpty = (values) => values.find(value => (
    value !== undefined
    && value !== null
    && String(value).trim() !== ''
    && String(value).trim() !== '0'
));

const normalizeDocumentLivePath = (value, isFolder = false) => {
    const path = String(value || '');
    if (!path) return '';
    // S3 keys are opaque byte identities. Preserve spaces, percent bytes,
    // backslashes, and repeated separators; only the conventional folder
    // marker slash is omitted from a folder identity.
    return isFolder && path.endsWith('/') ? path.slice(0, -1) : path;
};

const itemName = (item) => String(
    item?.name
    || item?.file?.name
    || item?.file_name
    || item?.file?.file_name
    || ''
);

export const getDocumentLiveItemIdentity = (type, item) => {
    const normalizedType = type === 'folder' ? 'folder' : 'file';
    const nested = item?.file && typeof item.file === 'object' ? item.file : null;
    const stableId = firstNonEmpty(normalizedType === 'folder'
        ? [item?.folder_id, item?.id, nested?.folder_id, nested?.id]
        : [
            item?.file_id,
            item?.document_id,
            item?.s3_object_id,
            item?.id,
            nested?.file_id,
            nested?.document_id,
            nested?.s3_object_id,
            nested?.id
        ]);
    const canonicalPath = normalizeDocumentLivePath(firstNonEmpty([
        item?.canonical_key,
        item?.object_key,
        item?.s3_key,
        item?.file_path,
        item?.key,
        item?.path,
        nested?.canonical_key,
        nested?.object_key,
        nested?.s3_key,
        nested?.file_path,
        nested?.key,
        nested?.path
    ]), normalizedType === 'folder');
    const aliases = [];

    if (stableId !== undefined) aliases.push(`${normalizedType}:id:${String(stableId).trim()}`);
    if (canonicalPath) aliases.push(`${normalizedType}:path:${canonicalPath}`);
    if (aliases.length === 0) {
        const name = itemName(item);
        if (name) aliases.push(`${normalizedType}:name:${name}`);
    }

    return {
        id: aliases[0] || '',
        aliases: [...new Set(aliases)]
    };
};

export const getDocumentLiveItemId = (type, item) => {
    return getDocumentLiveItemIdentity(type, item).id;
};

export const buildDocumentFolderSnapshot = (folderData = {}) => {
    const entries = [];

    collectionValues(folderData.folders).forEach(folder => {
        const id = getDocumentLiveItemId('folder', folder);
        if (!id) return;
        entries.push({
            id,
            aliases: getDocumentLiveItemIdentity('folder', folder).aliases,
            signature: [
                id,
                folder?.key || '',
                folder?.last_modified || '',
                folder?.size ?? ''
            ].join('|')
        });
    });

    collectionValues(folderData.files).forEach(file => {
        if (!file || file.name === '(Folder)') return;
        const id = getDocumentLiveItemId('file', file);
        if (!id) return;
        entries.push({
            id,
            aliases: getDocumentLiveItemIdentity('file', file).aliases,
            signature: [
                id,
                file?.key || file?.file_path || '',
                file?.last_modified || '',
                file?.size ?? ''
            ].join('|')
        });
    });

    collectionValues(folderData.metaData).forEach(meta => {
        const path = meta?.file_path || meta?.key || '';
        if (!path) return;
        entries.push({
            id: '',
            signature: [
                'metadata',
                path,
                meta?.updated_at || meta?.modified_at || '',
                meta?.tags || '',
                meta?.note_count ?? '',
                meta?.new ?? ''
            ].join('|')
        });
    });

    entries.sort((left, right) => left.signature.localeCompare(right.signature));

    return {
        ids: new Set(entries.map(entry => entry.id).filter(Boolean)),
        items: entries
            .filter(entry => entry.id)
            .map(entry => ({ id: entry.id, aliases: entry.aliases || [entry.id] })),
        fingerprint: entries.map(entry => entry.signature).join('\n')
    };
};

export const findNewDocumentItemIds = (previousIds, nextIds) => {
    const previous = previousIds instanceof Set ? previousIds : new Set(previousIds || []);
    const next = nextIds instanceof Set ? nextIds : new Set(nextIds || []);
    return new Set([...next].filter(id => !previous.has(id)));
};

export const getDocumentLiveScopeKey = ({ folderPath, page = 1, pageSize = 100 } = {}) => {
    const rawPath = String(folderPath || '{{Name}}');
    const normalizedPath = rawPath.endsWith('/') ? rawPath.slice(0, -1) : rawPath;
    const normalizedPage = Math.max(1, Number(page) || 1);
    const normalizedPageSize = Math.max(1, Number(pageSize) || 100);
    return `${normalizedPath}\n${normalizedPage}\n${normalizedPageSize}`;
};

// New markers are intentionally page-lifetime state. A listing refresh may add
// or reconcile them, but elapsed time and later polls do not mark an item read.
// A fresh registry (full page/component remount) starts a fresh baseline.
export const createDocumentLiveUnreadRegistry = () => {
    const snapshotsByScope = new Map();
    const parentByIdentity = new Map();
    const knownIds = new Set();
    const unreadIds = new Set();
    const seenIds = new Set();

    const ensureIdentity = (identity) => {
        if (identity && !parentByIdentity.has(identity)) parentByIdentity.set(identity, identity);
    };

    const rootFor = (identity) => {
        if (!identity) return '';
        ensureIdentity(identity);
        const parent = parentByIdentity.get(identity);
        if (parent === identity) return identity;
        const root = rootFor(parent);
        parentByIdentity.set(identity, root);
        return root;
    };

    const mergeStateInto = (targetRoot, sourceRoot) => {
        if (!sourceRoot || targetRoot === sourceRoot) return targetRoot;
        parentByIdentity.set(sourceRoot, targetRoot);
        if (knownIds.delete(sourceRoot)) knownIds.add(targetRoot);
        if (unreadIds.delete(sourceRoot)) unreadIds.add(targetRoot);
        if (seenIds.delete(sourceRoot)) seenIds.add(targetRoot);
        if (seenIds.has(targetRoot)) unreadIds.delete(targetRoot);
        return targetRoot;
    };

    const registerIdentity = (identity = {}) => {
        const aliases = [...new Set([
            identity?.id,
            ...(Array.isArray(identity?.aliases) ? identity.aliases : [])
        ].filter(Boolean))];
        if (aliases.length === 0) return '';

        aliases.forEach(ensureIdentity);
        let canonicalRoot = rootFor(aliases[0]);
        aliases.slice(1).forEach(alias => {
            const aliasRoot = rootFor(alias);
            canonicalRoot = mergeStateInto(canonicalRoot, aliasRoot);
        });
        aliases.forEach(alias => parentByIdentity.set(alias, canonicalRoot));
        return canonicalRoot;
    };

    const snapshotItems = (snapshot) => {
        if (Array.isArray(snapshot?.items)) return snapshot.items;
        return [...(snapshot?.ids || [])].map(id => ({ id, aliases: [id] }));
    };

    const visibleUnreadFor = (scopeKey) => {
        const snapshot = snapshotsByScope.get(scopeKey);
        const visible = new Set();
        snapshotItems(snapshot).forEach(identity => {
            const root = registerIdentity(identity);
            if (!root || !unreadIds.has(root) || seenIds.has(root)) return;
            // Include every representation supplied by this listing so a child
            // rendering an ID-preferred or path-fallback object can match it.
            [identity.id, ...(identity.aliases || [])].filter(Boolean).forEach(id => visible.add(id));
        });
        return visible;
    };

    return {
        record(scopeKey, snapshot, { announceNew = false } = {}) {
            const nextSnapshot = snapshot || { ids: new Set(), fingerprint: '' };
            const previousSnapshot = snapshotsByScope.get(scopeKey);
            const unchanged = Boolean(
                previousSnapshot
                && previousSnapshot.fingerprint === nextSnapshot.fingerprint
            );
            const previousRootsBeforeAliases = new Set(snapshotItems(previousSnapshot).map(registerIdentity).filter(Boolean));
            const nextIdentities = snapshotItems(nextSnapshot);
            const nextRoots = new Set(nextIdentities.map(registerIdentity).filter(Boolean));
            const previousRoots = new Set([...previousRootsBeforeAliases].map(rootFor));

            if (announceNew && previousSnapshot && !unchanged) {
                nextRoots.forEach(root => {
                    const normalizedRoot = rootFor(root);
                    if (
                        !previousRoots.has(normalizedRoot)
                        && !knownIds.has(normalizedRoot)
                        && !seenIds.has(normalizedRoot)
                    ) {
                        unreadIds.add(normalizedRoot);
                    }
                });
            }

            nextRoots.forEach(root => knownIds.add(rootFor(root)));
            snapshotsByScope.set(scopeKey, nextSnapshot);
            return { unchanged, unreadIds: visibleUnreadFor(scopeKey) };
        },

        markSeen(identity) {
            const normalizedIdentity = typeof identity === 'string'
                ? { id: identity, aliases: [identity] }
                : identity;
            const root = registerIdentity(normalizedIdentity);
            if (root) {
                seenIds.add(root);
                knownIds.add(root);
                unreadIds.delete(root);
            }
            return new Set([...unreadIds].map(rootFor));
        },

        getUnread() {
            return new Set([...unreadIds].map(rootFor).filter(root => !seenIds.has(root)));
        },

        getVisibleUnread(scopeKey) {
            return visibleUnreadFor(scopeKey);
        },

        clear() {
            snapshotsByScope.clear();
            parentByIdentity.clear();
            knownIds.clear();
            unreadIds.clear();
            seenIds.clear();
        }
    };
};

export const documentLiveSetsEqual = (left, right) => {
    const a = left instanceof Set ? left : new Set(left || []);
    const b = right instanceof Set ? right : new Set(right || []);
    return a.size === b.size && [...a].every(value => b.has(value));
};
